import { useState, useEffect, useContext } from "react";
import { useNavigate } from "react-router-dom";
import { getUserProfile, updateUserProfile, updatePreferredLanguage, getAssetUrl } from "../services/api";
import { ICONS } from "../icons";
import Button from "../components/UI/Button";
import FloatingInput from "../components/UI/FloatingInput";
import SelectBox from "../components/UI/SelectBox";
import { notify } from "../utils/notify";
import { useTranslation, I18nContext } from "../context/I18nContext";
import { AuthContext } from "../context/AuthContext";

export default function UpdateProfile() {
  const navigate = useNavigate();
  const token = localStorage.getItem("token");
  const { t } = useTranslation("profile");
  const { languages, languageCode, switchLanguage } = useContext(I18nContext);
  // بتحدّث نسخة البروفايل المشتركة في AuthContext بعد الحفظ، عشان صورة
  // الهيدر (ProfileMenu في MainLayout) تتحدث فورًا من غير ما المستخدم
  // يعمل ريفريش للصفحة.
  const { setUserProfile } = useContext(AuthContext);

  const [formData, setFormData] = useState({
    username: "",
    email: "",
    password: "",
    phone: "",
    jobTitle: "",
  });

  const [avatarPreview, setAvatarPreview] = useState(null);
  const [avatarFile, setAvatarFile] = useState(null);
  // Set when the user clicks "Reset Photo" on an avatar that's already
  // saved server-side — tells handleSubmit to explicitly ask the backend to
  // clear avatar_url. Without this, clearing avatarPreview/avatarFile only
  // affects the local preview: handleSubmit never appends an "avatar" field
  // when there's no new file, so the old avatar_url in the database (and
  // the picture that comes back after saving) never actually changes.
  const [removeAvatar, setRemoveAvatar] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [selectedLanguage, setSelectedLanguage] = useState(languageCode);
  const [savingLanguage, setSavingLanguage] = useState(false);

  // Keep the picker in sync if the language was switched some other way
  // (e.g. the header's LanguagePicker) while this page stays mounted.
  useEffect(() => {
    setSelectedLanguage(languageCode);
  }, [languageCode]);

  useEffect(() => {
    getUserProfile(token).then(u => {
      setFormData({
        username: u.username,
        email: u.email,
        phone: u.phone || "",
        jobTitle: u.job_title || "",
        gender: u.gender || "male",
        password: ""
      });
      if (u.avatar_url) setAvatarPreview(u.avatar_url);
    }).catch(() => notify.error(t("load_error", "Failed to load profile")));
  }, [token]);

  const handleLanguageSave = async () => {
    if (!selectedLanguage || selectedLanguage === languageCode) return;
    setSavingLanguage(true);
    try {
      await updatePreferredLanguage(token, selectedLanguage);
      switchLanguage(selectedLanguage);
      notify.success(t("language_save_success", "Language preference updated"));
    } catch (err) {
      notify.error(t("language_save_error", "Failed to update language preference"));
    } finally {
      setSavingLanguage(false);
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setAvatarFile(file);
      setAvatarPreview(URL.createObjectURL(file));
      setRemoveAvatar(false);
    }
  };

  const handleSubmit = async () => {
    setErrors({});
    let tempErrors = {};

    if (!formData.username.trim()) tempErrors.username = t("username_required_error", "Username is required");

    if (Object.keys(tempErrors).length > 0) {
      setErrors(tempErrors);
      notify.error(t("fix_errors_error", "Please fix the errors below"));
      return;
    }

    setLoading(true);
    
    const dataToSend = new FormData();
    dataToSend.append("username", formData.username);
    dataToSend.append("phone", formData.phone);
    dataToSend.append("jobTitle", formData.jobTitle);
    
    if (formData.password) {
      dataToSend.append("password", formData.password);
    }

    if (avatarFile) {
      dataToSend.append("avatar", avatarFile);
    } else if (removeAvatar) {
      dataToSend.append("removeAvatar", "true");
    }

    try {
      const updatedUser = await updateUserProfile(token, dataToSend);
      if (updatedUser) setUserProfile(updatedUser);
      notify.success(t("update_success", "Profile updated successfully"));
      setFormData(prev => ({ ...prev, password: "" }));
      setRemoveAvatar(false);
    } catch (err) {
      const errorMsg = err.data?.error || t("update_error", "Error updating profile");
      const lowMsg = errorMsg.toLowerCase();

      if (lowMsg.includes("username")) setErrors({ username: errorMsg });
      if (lowMsg.includes("phone")) setErrors({ phone: errorMsg });
      
      notify.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-8 max-w-5xl mx-auto">
     

      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <button onClick={() => navigate("/dashboard")} className="p-2 hover:bg-gray-100 rounded-xl transition-colors text-gray-400">
            <ICONS.ChevronLeft className="w-6 h-6 rtl:rotate-180" />
          </button>
          <div>
            <h2 className="text-2xl font-black text-gray-800 tracking-tight">{t("page_title", "Profile Settings")}</h2>
            <p className="text-sm text-gray-400 font-medium italic">{t("page_subtitle", "Update your personal and professional information")}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        
        {/* الكارد الجانبي - الصورة */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col items-center text-center">
            <div className="relative group mb-6">
              <div className="w-40 h-40 rounded-[3rem] overflow-hidden ring-8 ring-gray-50 bg-gray-100 flex items-center justify-center transition-all group-hover:ring-[var(--primary-color)]/10">
              {avatarPreview ? (
                  <img 
                    src={getAssetUrl(avatarPreview)}
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                ) : (
                  <img 
                    src={getAssetUrl(formData.gender === "female" ? "/img/avatars/Female-2.png" : "/img/avatars/Male.jpg")}
                    alt="Avatar" 
                    className="w-full h-full object-cover" 
                  />
                )}
              </div>
              
              <label className="absolute bottom-1 right-1 bg-[var(--primary-color)] text-white p-3 rounded-2xl shadow-xl cursor-pointer hover:scale-110 transition-transform">
                <ICONS.Plus className="w-5 h-5" />
                <input type="file" className="hidden" onChange={handleImageChange} accept="image/*" />
              </label>
            </div>

            <h3 className="font-black text-gray-700 uppercase text-xs tracking-widest">{t("profile_picture_label", "Profile Picture")}</h3>
            <p className="text-[10px] text-gray-400 mt-2 leading-relaxed font-medium">{t("avatar_hint", "PNG, JPG or GIF. Max 2MB.")}</p>

            {avatarPreview && (
              <button
                onClick={() => {setAvatarFile(null); setAvatarPreview(null); setRemoveAvatar(true);}}
                className="mt-4 text-[10px] font-black uppercase text-red-400 hover:text-red-600 transition-colors"
              >
                {t("reset_photo", "Reset Photo")}
              </button>
            )}
          </div>
          <div className="bg-blue-50/50 p-6 rounded-[2rem] border border-blue-100/30">
            <div className="flex gap-4">
              <ICONS.Lock className="w-5 h-5 text-blue-500 shrink-0" />
              <p className="text-[11px] text-blue-800/80 leading-relaxed font-medium">
                <strong>{t("privacy_note_label", "Privacy Note:")}</strong> {t("privacy_note_text", "Your email address is linked to your identity and cannot be changed for security reasons.")}
              </p>
            </div>
          </div>

          <div className="bg-white p-6 rounded-[2rem] border border-gray-100 shadow-sm">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] mb-4 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-gray-200"></span>
              {t("section_language", "Preferred Language")}
            </h3>
            <p className="text-[11px] text-gray-400 mb-4 leading-relaxed font-medium">
              {t("language_hint", "Choose which language the system shows you — pages not yet translated will keep showing in English.")}
            </p>
            <SelectBox
              label={t("section_language", "Preferred Language")}
              name="preferredLanguage"
              value={selectedLanguage}
              onChange={(val) => setSelectedLanguage(val)}
              options={(languages || [])
                .filter((l) => l.is_active)
                .map((l) => ({ value: l.code, label: l.name }))}
              icon={ICONS.Geography}
            />
            <Button
              variant="secondary"
              className="w-full mt-2"
              onClick={handleLanguageSave}
              loading={savingLanguage}
              isLoading={savingLanguage}
              disabled={!selectedLanguage || selectedLanguage === languageCode}
            >
              {t("language_save_button", "Save Language")}
            </Button>
          </div>
        </div>

        {/* الكارد الرئيسي - البيانات */}
        <div className="lg:col-span-2">
          <div className="bg-white p-8 rounded-[2.5rem] border border-gray-100 shadow-sm flex flex-col">
            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
              <span className="w-8 h-[2px] bg-gray-200"></span>
              {t("section_contact_info", "Professional & Contact Info")}
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
              <FloatingInput
                label={t("field_username", "Full Username")}
                value={formData.username}
                onChange={e => {
                  setFormData({...formData, username: e.target.value});
                  if(errors.username) setErrors({});
                }}
                error={errors.username}
                icon={ICONS.EditUser}
              />

              <FloatingInput
                label={t("field_job_title", "Job Title")}
                value={formData.jobTitle}
                onChange={e => setFormData({...formData, jobTitle: e.target.value})}
                icon={ICONS.Plus}
              />

              <FloatingInput
                label={t("field_phone", "Phone Number")}
                value={formData.phone}
                onChange={e => setFormData({...formData, phone: e.target.value})}
                icon={ICONS.Plus}
              />

              <FloatingInput
                label={t("field_email", "Registered Email")}
                value={formData.email}
                readOnly
                icon={ICONS.Email}
              />
            </div>

            <h3 className="text-xs font-black text-gray-400 uppercase tracking-[0.3em] mb-8 flex items-center gap-3">
               <span className="w-8 h-[2px] bg-gray-200"></span>
               {t("section_security", "Security Update")}
            </h3>

            <FloatingInput
              label={t("field_password", "Update Password (Leave blank to keep current)")}
              type="password"
              value={formData.password}
              onChange={e => setFormData({...formData, password: e.target.value})}
              icon={ICONS.Lock}
            />

            <div className="flex gap-4 mt-12 pt-8 border-t border-gray-50">
              <Button
                variant="primary"
                className="flex-grow shadow-lg shadow-blue-500/10"
                onClick={handleSubmit}
                loading={loading}
                icon={ICONS.Save}
              >
                {t("save_button", "Save Profile Changes")}
              </Button>
              <Button variant="secondary" onClick={() => navigate("/dashboard")}>
                {t("cancel_button", "Cancel")}
              </Button>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}